import type { ProductRewardSearchResult } from '../../types';
import type { UserRetailerShoppingCart } from './type';

type ProductMap = { [sku: string]: ProductRewardSearchResult };

/**
 * Make the cart's product metadata reachable by the sku the CART ITEMS carry.
 *
 * `getAllUserRetailerShoppingCarts` returns the line items and a map of product
 * metadata, and every consumer looks that map up by `item.stacklineSku` — nine
 * of them, from the bag drawer to the checkout summary. That works only while
 * both sides agree on which catalog minted the sku, and they do not always:
 *
 *   cart item   stacklineSku "2VDVD07E0"  retailerSku "46611416383665"   (V2)
 *   products    keyed        "E3NJ32BNE"  retailerSku "46611416383665"   (V1)
 *
 * Same listing, two catalogs, two stackline skus. The lookup misses, metadata
 * resolves to the bare cart line, which has no `title` — and `CartProduct`
 * renders its loading skeleton forever. The bag looks permanently stuck even
 * though the cart, the count and the subtotal are all correct.
 *
 * `retailerSku` is the thing both sides DO agree on: it is the retailer's own
 * listing id, and it is what the server resolved the metadata by in the first
 * place (note the map's own type is `{ [retailerSku: string]: … }` — the name
 * has always said so). So alias each product under the cart item's stackline
 * sku when that key is missing.
 *
 * Purely additive: existing keys are never overwritten, so a cart whose skus
 * already line up is returned untouched.
 */
export function alignProductsToCartSkus(
  products: ProductMap | undefined,
  carts: UserRetailerShoppingCart[] | undefined,
): ProductMap {
  const source = products ?? {};
  const items = (carts ?? []).flatMap((cart) => cart?.items ?? []);
  if (items.length === 0) return source;

  // One pass over the products, indexed by the key both sides share.
  const byRetailerSku = new Map<string, ProductRewardSearchResult>();
  for (const product of Object.values(source)) {
    const retailerSku = product?.retailerSku;
    if (retailerSku && !byRetailerSku.has(retailerSku)) {
      byRetailerSku.set(retailerSku, product);
    }
  }

  let aliased: ProductMap | undefined;
  for (const item of items) {
    const sku = item?.stacklineSku;
    if (!sku || source[sku]) continue;
    const match = item.retailerSku ? byRetailerSku.get(item.retailerSku) : undefined;
    if (!match) continue;
    aliased = aliased ?? { ...source };
    aliased[sku] = match;
  }

  return aliased ?? source;
}
